import tippy from 'tippy.js'
import 'tippy.js/animations/shift-away.css'
import 'tippy.js/dist/tippy.css'
import { BaseWebComponent } from '../components/BaseWebComponent.js'
import { aoiStore } from '../stores/AOIStore.js'
import { configStore } from '../stores/ConfigStore.js'
import { TableData } from '../utils/types.js'
import { queryElement, tippyToolbarOptions } from '../utils/utils.js'
import { DataTable } from '../visualizations/DataTable.js'

const template = `
<style>
  .cqmt-toolbar-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding-right: 12px;
    font-size: 1.5rem;
    background-color: #FFFFFFF2;
  }

  .cqmt-tools-container {
    display: flex;
    padding: 12px 20px;
    gap: 6px;
  }

  .cqmt-left-slot-container {
    margin-right: auto;
  }

  .cqmt-toolbar-separator {
    border-right: 1px solid var(--bunker-300);
  }

  .cqmt-end-slot-container {
    border-left: 1px solid var(--bunker-300);
  }

  .cqmt-text-button {
    display: flex;
    align-items: center;
  }

  .cqmt-toolbar-text {
    padding-right: 8px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.125rem;
    letter-spacing: 1.5%;
    color: var(--outerspace);
  }

  button,
  ::slotted(button) {
    height: 40px;
    min-width: 40px;
    padding: 8px;
    border: 1px solid var(--bunker-300);
    border-radius: 6px;
    background-color: #FFFFFF;
    cursor: pointer;
  }

  button:hover, ::slotted(button:hover) {
    background: rgba(0, 0, 0, 0.1);
  }

  button:focus-visible, ::slotted(button:focus-visible) {
    outline: var(--patina-600) solid 1px;
    outline-offset: 2px;
  }

  button:hover:focus-visible, ::slotted(button:hover:focus-visible) {
    background: #f9f6f6;
    outline: var(--patina-600) solid 1px;
    outline-offset: 2px;
  }

  #dataTable .cqmt-data-table-icon {
    transition: transform 150ms ease;
    transform: rotate(180deg);
  }
  #dataTable.data-table-open .cqmt-data-table-icon {
    transform: rotate(0deg);
  }

  @keyframes cqmt-popover-in {
    from {
      opacity: 0;
      transform: translateY(-6px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  dialog[popover] {
    margin: 0;
    padding: 6px;
    border: none;
    border-radius: 6px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }

  dialog[popover]:popover-open {
    animation: cqmt-popover-in 150ms ease;
    will-change: transform, opacity;

    &::backdrop {
      background: none;
    }
  }

  .popover-list {
    list-style: none;
    display: flex;
    gap: 8px;
    flex-direction: column;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 122%;
    letter-spacing: 0.24px;
    background: white;
    margin: 0;
    padding: 0;
    color: var(--bunker-700);
  }

  .popover-list-item {
    margin: 0;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 1px;

    &:hover, &:focus-visible {
      background: white;
      outline: 1px solid var(--patina-500);
      
      button {
        background: white;
        color: var(--patina-800);
      }
    }

    &:focus-within {
      background: white;
      outline: 1px solid var(--patina-500);
      
      button {
        &:focus-visible {
          outline: none;
        }

        background: white;
        color: var(--patina-800);
      }
    }

  }

  #draw-aoi-info-icon {
    padding: 4px;
    height: 100%;
    min-width: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;

    &:hover { background: var(--patina-50); }
  }

  button.popover-item {
    font: inherit;
    color: var(--bunker-700);
    border-radius: 1px;
    padding: 4px 12px;
    width: 100%;
    height: auto;
    font-weight: 400;
    cursor: pointer;
    border: none;
    text-align: left;
    // transition: all 0.2s ease;

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

</style>

<div class="cqmt-toolbar-container">
  <div class="cqmt-tools-container cqmt-left-slot-container">
    <slot name="left"></slot>
  </div>
  <div class="cqmt-tools-container cqmt-toolbar-separator">
    <slot name="start"></slot>
    <button id="areaOfInterest">
      <img src="CQMT/assets/HexagonIcon.svg" title="Area of Interest" />
    </button>
    <dialog
      id="aoiPopover"
      popover
      role="menu"
      aria-labelledby="areaOfInterest"
    >
      <ul class="popover-list">
        <li class="popover-list-item">
          <button
            type="button"
            role="menuitem"
            id="setToViewport"
            class="popover-item"
          >
            Set to Viewport
          </button>
        </li>
        <li class="popover-list-item">
          <button type="button" role="menuitem" id="drawAOI" class="popover-item">
            Draw AOI
          </button>
          <button id="draw-aoi-info-icon">
            <img src="CQMT/assets/InfoIcon-bunker700.svg" height="16px" width="16px" />
          </button>
        </li>
        <li class="popover-list-item">
          <button
            type="button"
            role="menuitem"
            id="uploadGeoJSON"
            class="popover-item"
          >
            Upload GeoJSON
          </button>
        </li>
        <li class="popover-list-item">
          <button type="button" role="menuitem" id="clearAOI" class="popover-item">
            Clear
          </button>
        </li>
        <li class="popover-list-item">
          <button
            type="button"
            role="menuitem"
            id="saveSelectionAsGeoJSON"
            class="popover-item disabled"
            aria-disabled="true"
          >
            Save Selection as GeoJSON
          </button>
        </li>
      </ul>
    </dialog>
    <slot name="end"></slot>
  </div>
  <div class="cqmt-tools-container">
    <button id="dataTable" class="cqmt-text-button">
      <span class="cqmt-toolbar-text">Data Table</span>
      <img class="cqmt-data-table-icon" src="CQMT/assets/DataTableIcon.svg" />
    </button>
  </div>
  <div class="cqmt-tools-container cqmt-end-slot-container">
    <slot name="far-right"></slot>
  </div>
</div>
`

export type ToolbarProps = {
  tableData?: TableData
  basemapOptions?: string[]
}

type ToolbarElement = HTMLElement & ToolbarProps

export class ToolbarMenu extends BaseWebComponent implements ToolbarElement {
  private _tableData: TableData
  private _basemapOptions: string[]
  private _subscriptions: (() => void)[]
  private _popoverStates: Record<'aoi' | 'basemap', boolean>

  protected get template(): string {
    return template
  }

  constructor() {
    super()

    this.style.cssText = `
      display: inline-flex;
      position: absolute;
      top: 0;
      left: 4.25rem;
      width: calc(100% - 4.25rem);
      z-index: 20;
    `

    this.attachShadow({ mode: 'open' })
    this.shadowRoot.innerHTML = template

    this._tableData = { columnNames: [], values: [] }
    this._basemapOptions = []
    this._subscriptions = []
    this._popoverStates = { aoi: false, basemap: false }
  }

  connectedCallback() {
    const unsubscribeFromUIConfigUpdates = configStore.subscribe(
      (state) => state.uiConfig,
      () => this.handleUIConfigUpdate(),
    )
    this._subscriptions.push(unsubscribeFromUIConfigUpdates)
    const unsubscribeFromAOIUpdates = aoiStore.subscribe(
      (state) => state.areaOfInterest,
      (areaOfInterest) => this.updateAOIControls(!!areaOfInterest),
    )
    this._subscriptions.push(unsubscribeFromAOIUpdates)

    this.shadowRoot
      .querySelector('#areaOfInterest')
      .addEventListener('click', () => {
        this.setPopoverPosition('aoi', true)
      })
    this.shadowRoot
      .querySelector('#setToViewport')
      .addEventListener('click', () => {
        this.dispatchAOIEvent('viewport')
      })
    this.shadowRoot.querySelector('#drawAOI').addEventListener('click', () => {
      this.dispatchAOIEvent('draw')
    })
    this.shadowRoot
      .querySelector('#uploadGeoJSON')
      .addEventListener('click', () => {
        this.dispatchAOIEvent('geoJSON')
      })
    this.shadowRoot.querySelector('#clearAOI').addEventListener('click', () => {
      this.dispatchAOIEvent('clear')
    })
    this.shadowRoot
      .querySelector('#saveSelectionAsGeoJSON')
      .addEventListener('click', () => {
        this.dispatchAOIEvent('saveSelection')
      })
    this.shadowRoot
      .querySelector('#dataTable')
      .addEventListener('click', () => {
        if (!document.querySelector('cqmt-data-table')) {
          const dataTable = document.createElement('cqmt-data-table')
          document.body.appendChild(dataTable)
          this.setDataTableOpenState(true)
        } else {
          document.querySelector('cqmt-data-table').remove()
          this.setDataTableOpenState(false)
        }
      })

    const drawAOIInfoIconButton = queryElement(
      this.shadowRoot,
      HTMLButtonElement,
      '#draw-aoi-info-icon',
    )
    const drawAOIListItem = drawAOIInfoIconButton?.closest(
      '.popover-list-item',
    ) as HTMLElement | null
    const drawAOIInfoIcon = drawAOIInfoIconButton?.querySelector(
      'img',
    ) as HTMLImageElement | null
    const defaultInfoIcon = 'CQMT/assets/InfoIcon-bunker700.svg'
    const hoverInfoIcon = 'CQMT/assets/InfoIcon-patina800.svg'
    const setInfoIcon = (isActive: boolean) => {
      if (!drawAOIInfoIcon) return
      drawAOIInfoIcon.src = isActive ? hoverInfoIcon : defaultInfoIcon
    }
    if (drawAOIListItem && drawAOIInfoIcon) {
      const handleEnter = () => setInfoIcon(true)
      const handleLeave = () => setInfoIcon(false)
      drawAOIListItem.addEventListener('mouseenter', handleEnter)
      drawAOIListItem.addEventListener('mouseleave', handleLeave)
      drawAOIListItem.addEventListener('focusin', handleEnter)
      drawAOIListItem.addEventListener('focusout', handleLeave)
      setInfoIcon(false)
      this._subscriptions.push(() => {
        drawAOIListItem.removeEventListener('mouseenter', handleEnter)
        drawAOIListItem.removeEventListener('mouseleave', handleLeave)
        drawAOIListItem.removeEventListener('focusin', handleEnter)
        drawAOIListItem.removeEventListener('focusout', handleLeave)
      })
    }

    drawAOIInfoIcon.onclick = () => {
      const closeEvent = new CustomEvent('cqmt-draw-aoi-info-icon-click', {
        bubbles: true,
        composed: true,
      })
      this.dispatchEvent(closeEvent)
    }

    window.addEventListener('resize', () => {
      this.setPopoverPositions()
    })

    // Track popover state so button clicks can reliably close/open
    const aoiPopover = queryElement(
      this.shadowRoot,
      HTMLDialogElement,
      '#aoiPopover',
    )
    if (aoiPopover) {
      aoiPopover.tabIndex = -1
      aoiPopover.addEventListener('toggle', (event: ToggleEvent) => {
        const isOpen = event.newState === 'open'
        this._popoverStates.aoi = isOpen
        if (isOpen) {
          this.focusPopover(aoiPopover)
        }
      })
    }
    const basemapPopover = queryElement(
      this.shadowRoot,
      HTMLDialogElement,
      '#basemapPopover',
    )
    if (basemapPopover) {
      basemapPopover.tabIndex = -1
      basemapPopover.addEventListener('toggle', (event: ToggleEvent) => {
        const isOpen = event.newState === 'open'
        this._popoverStates.basemap = isOpen
        if (isOpen) {
          this.focusPopover(basemapPopover)
        }
      })
    }

    this.setupPopoverKeyboardNavigation('aoiPopover')
    this.setupPopoverKeyboardNavigation('basemapPopover')

    const aoiButton = queryElement(
      this.shadowRoot,
      HTMLButtonElement,
      '#areaOfInterest',
    )
    tippy(aoiButton, {
      ...tippyToolbarOptions,
      content: 'Area of Interest Controls',
    })

    document.addEventListener(
      'cqmt-data-table-close',
      this.handleDataTableClose,
    )
    this.setDataTableOpenState(!!document.querySelector('cqmt-data-table'))
  }

  disconnectedCallback() {
    this._subscriptions.forEach((unsubscribe) => unsubscribe())
    document.removeEventListener(
      'cqmt-data-table-close',
      this.handleDataTableClose,
    )
  }

  updateAOIControls(isAreaOfInterestDefined: boolean) {
    if (isAreaOfInterestDefined) {
      queryElement(
        this.shadowRoot,
        HTMLElement,
        '#saveSelectionAsGeoJSON',
      )?.classList.remove('disabled')
    } else {
      queryElement(
        this.shadowRoot,
        HTMLElement,
        '#saveSelectionAsGeoJSON',
      )?.classList.add('disabled')
    }
  }

  handleUIConfigUpdate() {
    const { uiConfig } = configStore.getState()

    const toolbarContainer = queryElement(
      this.shadowRoot,
      HTMLElement,
      '.cqmt-toolbar-container',
    )

    if (toolbarContainer) {
      toolbarContainer.style.display = uiConfig.showToolbar ? 'flex' : 'none'
    }

    // Add Area of Interest controls visibility handling
    const aoiButton = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#areaOfInterest',
    )

    if (aoiButton) {
      aoiButton.style.display = uiConfig.showAreaOfInterestControls
        ? 'block'
        : 'none'
    }

    // Add Data Table button visibility handling
    const dataTableButton = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#dataTable',
    )

    if (dataTableButton) {
      dataTableButton.style.display = uiConfig.showDataTableButton
        ? 'flex'
        : 'none'
    }
  }

  dispatchAOIEvent(aoiEventType = '') {
    const aoiEvent = new CustomEvent('cqmt-aoi-selection', {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: { type: aoiEventType },
    })
    this.dispatchEvent(aoiEvent)
    queryElement(this.shadowRoot, HTMLElement, '#aoiPopover').hidePopover()
    this._popoverStates.aoi = false
  }

  setPopoverPositions() {
    this.setPopoverPosition('aoi')
    this.setPopoverPosition('basemap')
  }

  setPopoverPosition(target = 'aoi', togglePopover = false) {
    let popoverTriggerRect, popoverElement
    if (target === 'aoi') {
      popoverTriggerRect = this.shadowRoot
        ?.querySelector('#areaOfInterest')
        ?.getBoundingClientRect()
      popoverElement = this.shadowRoot.querySelector('#aoiPopover')
    } else if (target === 'basemap') {
      popoverTriggerRect = this.shadowRoot
        ?.querySelector('#basemap')
        ?.getBoundingClientRect()
      popoverElement = this.shadowRoot.querySelector('#basemapPopover')
    } else {
      return
    }

    if (popoverElement && popoverTriggerRect) {
      popoverElement.style.top = popoverTriggerRect.bottom + 14 + 'px'
      popoverElement.style.left = popoverTriggerRect.left - 40 + 'px'

      if (togglePopover) {
        const isOpen =
          this._popoverStates[target] || popoverElement.matches(':popover-open')
        if (isOpen) {
          if (popoverElement.dataset.closing === 'true') return

          popoverElement.dataset.closing = 'true'
          this._popoverStates[target] = false
        } else {
          popoverElement.showPopover()
          popoverElement.dataset.closing = 'false'
          this._popoverStates[target] = true
        }
      }
    }
  }

  private focusPopover(popoverElement: HTMLDialogElement) {
    popoverElement.tabIndex = -1
    popoverElement.focus({ preventScroll: true })
  }

  private setupPopoverKeyboardNavigation(
    popoverId: 'aoiPopover' | 'basemapPopover',
  ) {
    const popover = this.shadowRoot?.querySelector<HTMLElement>(`#${popoverId}`)
    if (!popover) return

    popover.addEventListener('keydown', (event) =>
      this.handlePopoverKeyboardNavigation(event, popoverId),
    )
  }

  private handlePopoverKeyboardNavigation(
    event: KeyboardEvent,
    popoverId: 'aoiPopover' | 'basemapPopover',
  ) {
    const { key } = event

    if (key === 'Tab') {
      event.preventDefault()
      return
    }

    if (key !== 'ArrowDown' && key !== 'ArrowUp') return

    const focusableItems = this.getEnabledPopoverItems(popoverId)
    if (!focusableItems.length) return

    event.preventDefault()

    const currentIndex = focusableItems.findIndex(
      (item) => item === event.target,
    )

    if (currentIndex === -1) {
      focusableItems[0].focus()
      return
    }

    const direction = key === 'ArrowDown' ? 1 : -1
    const nextIndex =
      (currentIndex + direction + focusableItems.length) % focusableItems.length

    focusableItems[nextIndex].focus()
  }

  private getEnabledPopoverItems(
    popoverId: 'aoiPopover' | 'basemapPopover',
  ): HTMLButtonElement[] {
    const popover = this.shadowRoot?.querySelector<HTMLElement>(`#${popoverId}`)
    if (!popover) {
      return []
    }

    return Array.from(
      popover.querySelectorAll<HTMLButtonElement>('.popover-item'),
    ).filter(
      (item) =>
        !item.classList.contains('disabled') &&
        item.getAttribute('aria-disabled') !== 'true',
    )
  }

  private setDataTableOpenState(isOpen: boolean) {
    const dataTableButton = queryElement(
      this.shadowRoot,
      HTMLButtonElement,
      '#dataTable',
    )

    dataTableButton?.classList.toggle('data-table-open', isOpen)
  }

  private handleDataTableClose = () => {
    this.setDataTableOpenState(false)
  }

  static get observedAttributes() {
    return ['tabledata', 'basemapoptions']
  }

  get tableData(): TableData {
    return this._tableData
  }
  set tableData(newVal: TableData) {
    this._tableData = newVal

    const dataTable = queryElement(document, DataTable, 'cqmt-data-table')
    if (dataTable) {
      dataTable.tableData = this.tableData
    }
  }

  get basemapOptions(): string[] {
    return this._basemapOptions
  }
  set basemapOptions(newVal: string[]) {
    this._basemapOptions = newVal
    this.setBasemapOptionDisplay()
  }

  setBasemapOptionDisplay() {
    const basemapPopoverContainer =
      this.shadowRoot.querySelector('#basemapPopover')
    const optionsList = basemapPopoverContainer?.querySelector('.popover-list')
    const noOptionsPlaceholder = queryElement(
      this.shadowRoot,
      HTMLElement,
      '#noOptionsPlaceholder',
    )
    if (!basemapPopoverContainer || !optionsList || !noOptionsPlaceholder) {
      return
    }

    optionsList.innerHTML = ''
    if (this.basemapOptions.length) {
      noOptionsPlaceholder.style.display = 'none'

      this.basemapOptions.forEach((option) => {
        const buttonContainer = document.createElement('li')
        buttonContainer.className = 'popover-list-item'

        const optionButton = document.createElement('button')
        optionButton.type = 'button'
        optionButton.id = option.replaceAll(' ', '')
        optionButton.className = 'popover-item'
        optionButton.innerText = option
        optionButton.addEventListener('click', () => {
          this.dispatchBasemapEvent(optionButton.id)
        })

        buttonContainer.appendChild(optionButton)
        optionsList.appendChild(buttonContainer)
      })
    } else {
      noOptionsPlaceholder.style.display = 'block'
      optionsList.appendChild(noOptionsPlaceholder)
    }
  }

  dispatchBasemapEvent(id) {
    const basemapEvent = new CustomEvent('cqmt-basemap-selection', {
      bubbles: true,
      cancelable: true,
      composed: true,
      detail: { type: id },
    })
    this.dispatchEvent(basemapEvent)
    queryElement(this.shadowRoot, HTMLElement, '#basemapPopover').hidePopover()
    this._popoverStates.basemap = false
  }
}

if (!window.customElements.get('cqmt-toolbar')) {
  window.customElements.define('cqmt-toolbar', ToolbarMenu)
}
